import { useCallback, useMemo } from 'react';
import { ListPlus, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { LineItemRow } from './LineItemRow';
import { ColumnSettings } from './ColumnSettings';
import { COLUMNS, CLIENT_COLUMNS } from '../hooks/useColumnConfig';
import { formatNumber, formatPercent, chfToPresentation, toBase } from '../lib/utils';

function ResizeHandle({ colId, currentWidth, onResizeEnd }) {
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = currentWidth;
    const th = e.target.closest('th');

    const onMouseMove = (ev) => {
      const newW = Math.max(40, startW + (ev.clientX - startX));
      if (th) th.style.width = `${newW}px`;
    };

    const onMouseUp = (ev) => {
      const finalW = Math.max(40, startW + (ev.clientX - startX));
      onResizeEnd(colId, finalW);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colId, currentWidth, onResizeEnd]);

  return (
    <div
      onMouseDown={onMouseDown}
      className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-400/40 z-20"
      data-testid="col-resize-handle"
    />
  );
}

function HeaderCell({ col, width, onResizeEnd, children }) {
  const content = children || col.label;
  const alignmentClass =
    col.id === 'qty'
      ? 'text-center'
      : ['rev_unit', 'discount_value', 'client_rev_unit', 'client_rev_total', 'rev_chf', 'cost_unit', 'cost_chf', 'margin_chf', 'margin_pct', 'actions'].includes(col.id)
        ? 'text-right'
        : 'text-left';

  const cell = (
    <th
      className={`pl-table-head-cell relative select-none ${alignmentClass}`}
      style={{ width: `${width}px`, minWidth: `${col.minWidth}px` }}
      data-testid={`col-header-${col.id}`}
    >
      {content}
      {col.resizable && <ResizeHandle colId={col.id} currentWidth={width} onResizeEnd={onResizeEnd} />}
    </th>
  );

  if (!col.tooltip) return cell;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{cell}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-64">{col.tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function LineItemTable({
  items,
  categories,
  vendors,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onDuplicateItem,
  onRestoreItem,
  onCreateCategory,
  onDeleteCategory,
  onCreateVendor,
  onImportFromMasterPriceList,
  loading,
  marginTarget,
  presentationCurrency,
  vendorCurrency,
  coreCurrency = 'CHF',
  fxRates,
  dealTotals,
  columnConfig,
  title = 'Line Items',
  addRowLabel = 'Add Row',
  emptyMessage = 'No line items yet. Click "Add Row" to start.',
  nameLayout = 'compact',
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onImportSelected,
  importSelectedLabel = 'Import Selected Items',
}) {
  const { widths, hidden, setColumnWidth, toggleColumn, resetLayout, isVisible, columnDefs: configColumnDefs } = columnConfig;
  const showClientView = presentationCurrency !== coreCurrency;
  const showVendorView = vendorCurrency !== coreCurrency;

  /** Sum of (qty × cost_unit); one number only when all rows use the same cost_currency. */
  const costUnitColumnFooter = useMemo(() => {
    if (!items.length) return null;
    const curs = [
      ...new Set(
        items.map((it) => String(it.cost_currency || coreCurrency || 'CHF').trim().toUpperCase()),
      ),
    ];
    const singleCur = curs.length === 1 ? curs[0] : null;
    const sumNative = items.reduce(
      (s, it) => s + (Number(it.qty) || 0) * (Number(it.cost_unit) || 0),
      0,
    );
    let sumCore = null;
    if (fxRates && singleCur) {
      sumCore = 0;
      for (const it of items) {
        const q = Number(it.qty) || 0;
        const u = Number(it.cost_unit) || 0;
        const cur = String(it.cost_currency || coreCurrency || 'CHF').trim().toUpperCase();
        sumCore += toBase(q * u, cur, coreCurrency, fxRates);
      }
    }
    return { singleCur, sumNative, sumCore };
  }, [items, fxRates, coreCurrency]);

  const vis = (colId) => isVisible(colId);
  const w = (colId) => widths[colId] || 80;
  const allColumnDefs = configColumnDefs || [...COLUMNS, ...CLIENT_COLUMNS];
  const colById = (id) => allColumnDefs.find((col) => col.id === id) || COLUMNS.find((col) => col.id === id);

  const selectableItems = useMemo(
    () => (selectable ? items.filter((it) => it.is_active !== false) : []),
    [selectable, items],
  );
  const importableSelectedCount = useMemo(() => {
    if (!selectable || !selectedIds?.size) return 0;
    return selectableItems.filter((it) => selectedIds.has(it.id)).length;
  }, [selectable, selectedIds, selectableItems]);
  const allSelectableSelected = selectableItems.length > 0
    && selectableItems.every((it) => selectedIds?.has(it.id));
  const someSelectableSelected = selectableItems.some((it) => selectedIds?.has(it.id));
  const selectAllChecked = allSelectableSelected
    ? true
    : someSelectableSelected
      ? 'indeterminate'
      : false;

  // Count visible columns for colSpan on empty/totals rows
  const visibleCount = COLUMNS.filter(c => vis(c.id)).length
    + (showClientView ? CLIENT_COLUMNS.filter(c => vis(c.id)).length : 0)
    + (selectable ? 1 : 0);

  // Group header colSpan helpers
  const identityVisible = ['name', 'category', 'qty'].filter(id => vis(id)).length;
  const clientVisible = showClientView ? ['client_rev_unit', 'client_rev_total'].filter(id => vis(id)).length : 0;
  const revenueVisible = ['rev_unit', 'rev_cur', 'discount_type', 'discount_value', 'rev_chf'].filter(id => vis(id)).length;
  const costVisible = ['cost_unit', 'cost_cur', 'cost_chf', 'vendor'].filter(id => vis(id)).length;
  const marginVisible = ['margin_chf', 'margin_pct'].filter(id => vis(id)).length;
  const actionsVisible = vis('actions') ? 1 : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-hidden bg-white p-2.5 lg:gap-2 lg:p-3">
      {/* Header */}
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="pl-section-title" data-testid="line-items-header">
          {title}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <ColumnSettings
            hidden={hidden}
            onToggle={toggleColumn}
            onReset={resetLayout}
            showClientView={showClientView}
          />
          {onImportFromMasterPriceList ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onImportFromMasterPriceList}
              disabled={loading}
              className="h-8 gap-1.5 px-2.5 text-xs sm:h-9"
              data-testid="import-master-price-list-btn"
            >
              <ListPlus className="h-4 w-4" />
              Import from Master Price List
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={onAddItem}
            disabled={loading}
            className="h-8 gap-1.5 px-2.5 text-xs sm:h-9"
            data-testid="add-line-item-btn"
          >
            <Plus className="h-4 w-4" />
            {addRowLabel}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="pl-table-shell min-h-0 flex-1 overflow-auto">
        <table className="data-table" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }} data-testid="line-items-table">
          <colgroup>
            {selectable ? <col style={{ width: '36px' }} /> : null}
            {vis('name') && <col style={{ width: `${w('name')}px` }} />}
            {vis('category') && <col style={{ width: `${w('category')}px` }} />}
            {vis('qty') && <col style={{ width: `${w('qty')}px` }} />}
            {showClientView && vis('client_rev_unit') && <col style={{ width: `${w('client_rev_unit')}px` }} />}
            {showClientView && vis('client_rev_total') && <col style={{ width: `${w('client_rev_total')}px` }} />}
            {vis('rev_unit') && <col style={{ width: `${w('rev_unit')}px` }} />}
            {vis('rev_cur') && <col style={{ width: `${w('rev_cur')}px` }} />}
            {vis('discount_type') && <col style={{ width: `${w('discount_type')}px` }} />}
            {vis('discount_value') && <col style={{ width: `${w('discount_value')}px` }} />}
            {vis('rev_chf') && <col style={{ width: `${w('rev_chf')}px` }} />}
            {vis('cost_unit') && <col style={{ width: `${w('cost_unit')}px` }} />}
            {vis('cost_cur') && <col style={{ width: `${w('cost_cur')}px` }} />}
            {vis('cost_chf') && <col style={{ width: `${w('cost_chf')}px` }} />}
            {vis('vendor') && <col style={{ width: `${w('vendor')}px` }} />}
            {vis('margin_chf') && <col style={{ width: `${w('margin_chf')}px` }} />}
            {vis('margin_pct') && <col style={{ width: `${w('margin_pct')}px` }} />}
            {vis('actions') && <col style={{ width: `${w('actions')}px` }} />}
          </colgroup>
          <thead className="pl-table-header">
            {/* Group header row */}
            <tr className="bg-gray-50">
              {selectable ? <th className="h-7 border-b border-gray-200 px-1" /> : null}
              {identityVisible > 0 && (
                <th colSpan={identityVisible} className="h-7 px-2.5 text-left text-xs font-medium text-gray-400" />
              )}
              {clientVisible > 0 && (
                <th colSpan={clientVisible} className="h-7 border-b border-l border-blue-200 bg-blue-50/50 px-2.5 text-center text-xs font-medium text-blue-600" data-testid="group-client-view">
                  Client ({presentationCurrency})
                </th>
              )}
              {revenueVisible > 0 && (
                <th colSpan={revenueVisible} className="h-7 border-b border-l border-gray-200 px-2.5 text-center text-xs font-medium text-gray-500" data-testid="group-revenue">
                  Revenue ({coreCurrency})
                </th>
              )}
              {costVisible > 0 && (
                <th colSpan={costVisible} className="h-7 border-b border-l border-gray-200 px-2.5 text-center text-xs font-medium text-gray-500" data-testid="group-cost">
                  Cost ({coreCurrency})
                </th>
              )}
              {marginVisible > 0 && (
                <th colSpan={marginVisible} className="h-7 border-b border-l border-gray-200 px-2.5 text-center text-xs font-medium text-gray-500" data-testid="group-margin">
                  Margin
                </th>
              )}
              {actionsVisible > 0 && (
                <th className="h-8 border-b border-gray-200 px-2" />
              )}
            </tr>
            {/* Column header row */}
            <tr className="bg-white">
              {selectable ? (
                <th className="pl-table-head-cell w-9 px-1 text-center">
                  <Checkbox
                    checked={selectAllChecked}
                    onCheckedChange={(v) => onToggleSelectAll?.(v === true)}
                    disabled={loading || selectableItems.length === 0}
                    aria-label="Select all visible catalog items"
                    data-testid="catalog-select-all"
                  />
                </th>
              ) : null}
              {vis('name') && (
                <HeaderCell col={colById('name')} width={w('name')} onResizeEnd={setColumnWidth}>
                  <div className="flex items-center justify-between gap-1">
                    <span>{colById('name')?.label || 'Name'}</span>
                    {onImportFromMasterPriceList ? (
                      <button
                        type="button"
                        onClick={onImportFromMasterPriceList}
                        disabled={loading}
                        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                        title="Import from Master Price List"
                        data-testid="import-master-price-list-name-btn"
                      >
                        <ListPlus className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                </HeaderCell>
              )}
              {vis('category') && <HeaderCell col={colById('category')} width={w('category')} onResizeEnd={setColumnWidth} />}
              {vis('qty') && <HeaderCell col={colById('qty')} width={w('qty')} onResizeEnd={setColumnWidth} />}

              {showClientView && vis('client_rev_unit') && (
                <HeaderCell col={CLIENT_COLUMNS[0]} width={w('client_rev_unit')} onResizeEnd={setColumnWidth}>
                  <span className="text-blue-500">Rev/Unit</span>
                </HeaderCell>
              )}
              {showClientView && vis('client_rev_total') && (
                <HeaderCell col={CLIENT_COLUMNS[1]} width={w('client_rev_total')} onResizeEnd={setColumnWidth}>
                  <span className="text-blue-500">Total Rev</span>
                </HeaderCell>
              )}

              {vis('rev_unit') && <HeaderCell col={colById('rev_unit')} width={w('rev_unit')} onResizeEnd={setColumnWidth} />}
              {vis('rev_cur') && <HeaderCell col={colById('rev_cur')} width={w('rev_cur')} onResizeEnd={setColumnWidth} />}
              {vis('discount_type') && <HeaderCell col={colById('discount_type')} width={w('discount_type')} onResizeEnd={setColumnWidth} />}
              {vis('discount_value') && <HeaderCell col={colById('discount_value')} width={w('discount_value')} onResizeEnd={setColumnWidth} />}
              {vis('rev_chf') && <HeaderCell col={colById('rev_chf')} width={w('rev_chf')} onResizeEnd={setColumnWidth} />}
              {vis('cost_unit') && <HeaderCell col={colById('cost_unit')} width={w('cost_unit')} onResizeEnd={setColumnWidth} />}
              {vis('cost_cur') && <HeaderCell col={colById('cost_cur')} width={w('cost_cur')} onResizeEnd={setColumnWidth} />}
              {vis('cost_chf') && <HeaderCell col={colById('cost_chf')} width={w('cost_chf')} onResizeEnd={setColumnWidth} />}
              {vis('vendor') && <HeaderCell col={colById('vendor')} width={w('vendor')} onResizeEnd={setColumnWidth} />}
              {vis('margin_chf') && <HeaderCell col={colById('margin_chf')} width={w('margin_chf')} onResizeEnd={setColumnWidth} />}
              {vis('margin_pct') && <HeaderCell col={colById('margin_pct')} width={w('margin_pct')} onResizeEnd={setColumnWidth} />}
              {vis('actions') && <HeaderCell col={colById('actions')} width={w('actions')} onResizeEnd={setColumnWidth} />}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <LineItemRow
                key={item.id}
                item={item}
                categories={categories}
                vendors={vendors}
                onUpdate={onUpdateItem}
                onDelete={onDeleteItem}
                onDuplicate={onDuplicateItem}
                onRestoreItem={onRestoreItem}
                onCreateCategory={onCreateCategory}
                onDeleteCategory={onDeleteCategory}
                onCreateVendor={onCreateVendor}
                disabled={loading}
                nameLayout={nameLayout}
                selectable={selectable}
                selected={selectedIds?.has(item.id)}
                onToggleSelect={onToggleSelect}
                canSelect={item.is_active !== false}
                marginTarget={marginTarget}
                presentationCurrency={presentationCurrency}
                vendorCurrency={vendorCurrency}
                coreCurrency={coreCurrency}
                fxRates={fxRates}
                visibleColumns={hidden}
              />
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={visibleCount} className="h-20 px-3 text-center text-sm text-gray-400">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
          {/* Totals row */}
          {dealTotals && items.length > 0 && (
            <tfoot>
              <tr className="h-9 border-t-2 border-gray-300 bg-gray-50" data-testid="totals-row">
                {selectable ? <td className="border-b border-gray-200 px-1" /> : null}
                {vis('name') && (
                  <td colSpan={[vis('name'), vis('category')].filter(Boolean).length} className="border-b border-gray-200 px-3 text-xs font-semibold text-gray-500">
                    Totals
                  </td>
                )}
                {vis('name') && vis('category') ? null : vis('category') && <td className="border-b border-gray-200 px-3 text-xs font-semibold text-gray-500">Totals</td>}
                {vis('qty') && <td className="border-b border-gray-200 px-3" />}

                {showClientView && vis('client_rev_unit') && <td className="border-b border-l border-blue-200 bg-blue-50/30 px-3" />}
                {showClientView && vis('client_rev_total') && (
                  <td className="border-b border-blue-200 bg-blue-50/30 px-3 text-right text-[13px] font-semibold tabular-nums text-blue-700" data-testid="total-client-rev">
                    {formatNumber(chfToPresentation(dealTotals.total_revenue_chf, presentationCurrency, fxRates, coreCurrency))}
                  </td>
                )}

                {vis('rev_unit') && <td className="border-b border-l border-gray-200 px-3" />}
                {vis('rev_cur') && <td className="border-b border-gray-200 px-1" />}
                {vis('discount_type') && <td className="border-b border-gray-200 px-1" />}
                {vis('discount_value') && <td className="border-b border-gray-200 px-3" />}
                {vis('rev_chf') && (
                  <td className="border-b border-gray-200 px-3 text-right text-[13px] font-semibold tabular-nums text-gray-900" data-testid="total-rev-chf">
                    {formatNumber(dealTotals.total_revenue_chf)}
                  </td>
                )}

                {vis('cost_unit') && (
                  <td
                    className="border-b border-l border-gray-200 px-3 text-right text-[13px] font-semibold tabular-nums text-gray-900"
                    data-testid="total-cost-unit"
                    title={
                      costUnitColumnFooter && !costUnitColumnFooter.singleCur
                        ? 'Mixed cost currencies — total extended cost is in Cost Total.'
                        : undefined
                    }
                  >
                    {costUnitColumnFooter && costUnitColumnFooter.singleCur != null ? (
                      <>
                        {formatNumber(costUnitColumnFooter.sumNative, 2)}
                        {showVendorView &&
                        costUnitColumnFooter.singleCur !== coreCurrency &&
                        costUnitColumnFooter.sumCore != null ? (
                          <span className="ml-1 text-xs font-normal text-orange-600">
                            (
                            {formatNumber(
                              chfToPresentation(
                                costUnitColumnFooter.sumCore,
                                vendorCurrency,
                                fxRates,
                                coreCurrency,
                              ),
                              2,
                            )}{' '}
                            {vendorCurrency})
                          </span>
                        ) : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                )}
                {vis('cost_cur') && (
                  <td
                    className="border-b border-gray-200 px-1 text-center text-xs font-medium text-gray-600"
                    data-testid="total-cost-cur"
                  >
                    {costUnitColumnFooter?.singleCur ?? ''}
                  </td>
                )}
                {vis('cost_chf') && (
                  <td className="border-b border-gray-200 px-3 text-right text-[13px] font-semibold tabular-nums text-gray-900" data-testid="total-cost-chf">
                    {formatNumber(dealTotals.total_cost_chf)}
                    {showVendorView && (
                      <span className="ml-1 text-xs font-normal text-orange-600" data-testid="total-cost-vendor">
                        ({formatNumber(chfToPresentation(dealTotals.total_cost_chf, vendorCurrency, fxRates, coreCurrency))} {vendorCurrency})
                      </span>
                    )}
                  </td>
                )}
                {vis('vendor') && <td className="border-b border-gray-200 px-3" />}

                {vis('margin_chf') && (
                  <td className="border-b border-l border-gray-200 px-3 text-right text-[13px] font-semibold tabular-nums text-gray-900" data-testid="total-margin-chf">
                    {formatNumber(dealTotals.total_margin_chf)}
                  </td>
                )}
                {vis('margin_pct') && (
                  <td className="border-b border-gray-200 px-3 text-right text-[13px] font-semibold tabular-nums text-gray-900" data-testid="total-margin-pct">
                    {formatPercent(dealTotals.total_margin_pct)}
                  </td>
                )}

                {vis('actions') && <td className="border-b border-gray-200 px-2" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {selectable && importableSelectedCount > 0 && onImportSelected ? (
        <div
          className="shrink-0 flex flex-wrap items-center justify-between gap-2 border-t border-slate-700 bg-slate-900 px-3 py-2.5 text-white shadow-[0_-4px_12px_rgba(15,23,42,0.15)]"
          data-testid="master-price-list-selection-bar"
        >
          <span className="text-sm font-medium tabular-nums">
            {importableSelectedCount} item{importableSelectedCount === 1 ? '' : 's'} selected
          </span>
          <Button
            type="button"
            size="sm"
            onClick={onImportSelected}
            disabled={loading}
            className="h-8 bg-white text-slate-900 hover:bg-slate-100"
            data-testid="import-selected-catalog-items"
          >
            {importSelectedLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
